import React, { lazy } from "react";
import { Navigate } from "react-router-dom";
import App from "./../app.jsx";
import CompanyFeatureGate from "../components/online-ordering/CompanyFeatureGate.jsx";

/** Reload once when a webpack chunk fails (common after hot reload or mid-compile). */
function lazyWithRetry(factory) {
	return lazy(() =>
		factory().catch((error) => {
			const message = String(error?.message || error || "");
			const isChunkError =
				error?.name === "ChunkLoadError" ||
				/Loading chunk [\w-]+ failed/i.test(message) ||
				/Failed to fetch dynamically imported module/i.test(message);

			if (isChunkError && typeof window !== "undefined") {
				const key = "chunk_reload_" + window.location.pathname;
				if (!sessionStorage.getItem(key)) {
					sessionStorage.setItem(key, "1");
					window.location.reload();
					return new Promise(() => {});
				}
				sessionStorage.removeItem(key);
			}
			throw error;
		})
	);
}

const Landing = lazyWithRetry(() => import("../pages/Landing/Landing.jsx"));
const Home = lazyWithRetry(() => import("../pages/Home/Home.jsx"));
const TableLanding = lazyWithRetry(() => import("../pages/TableLanding/TableLanding.jsx"));
const OnlineOrdering = lazyWithRetry(() => import("../pages/Menu/Menu.jsx"));
const Combos = lazyWithRetry(() => import("../pages/Combos/Combos.jsx"));
const Deals = lazyWithRetry(() => import("../pages/Deals/Deals.jsx"));
const DealSummary = lazyWithRetry(() => import("../pages/Deals/DealSummary.jsx"));
const DealSlotSelectorPage = lazyWithRetry(() =>
	import("../pages/Deals/DealSlotSelectorPage.jsx")
);
const CreateReservation = lazyWithRetry(() =>
	import("../pages/Reservation/CreateReservation.jsx")
);
const ReservationComplete = lazyWithRetry(() =>
	import("../pages/Reservation/ReservationComplete.jsx")
);
const Checkout = lazyWithRetry(() => import("../pages/Checkout/Checkout.jsx"));
const CheckoutComplete = lazyWithRetry(() => import("../pages/Checkout/CheckoutComplete.jsx"));
const MyOrders = lazyWithRetry(() => import("../pages/MyOrders/MyOrders.jsx"));
const TrackOrder = lazyWithRetry(() => import("../pages/TrackOrder/TrackOrder.jsx"));
const Profile = lazyWithRetry(() => import("../pages/Profile/Profile.jsx"));
const Notifications = lazyWithRetry(() => import("../pages/Notifications/Notifications.jsx"));
const CartPage = lazyWithRetry(() => import("../pages/Cart/CartPage.jsx"));
const OrderDetails = lazyWithRetry(() => import("../pages/OrderDetails/OrderDetails.jsx"));
const ResetPassword = lazyWithRetry(() => import("../pages/ResetPassword/ResetPassword.jsx"));
const CancelReservation = lazyWithRetry(() =>
	import("../pages/Reservation/CancelReservation.jsx")
);
const ExtraError = lazyWithRetry(() => import("../pages/extra/extra-error.js"));
const PizzaModifierTestPage = lazyWithRetry(() =>
	import("../pages/pizzaModifierPopup/PizzaModifierTestPage.jsx")
);

function withOnlineOrdering(element) {
	return (
		<CompanyFeatureGate feature="onlineOrdering">{element}</CompanyFeatureGate>
	);
}

function withReservation(element) {
	return (
		<CompanyFeatureGate feature="reservation">{element}</CompanyFeatureGate>
	);
}

const AppRoute = [
	{
		path: "/",
		element: <Landing />,
	},
	{
		path: "/:restaurantSlug/*",
		element: <App />,
		children: [
			{ index: true, element: <Navigate to="home" replace /> },
			{ path: "home", element: <Home /> },
			{ path: "table", element: withOnlineOrdering(<TableLanding />) },
			{ path: "menu", element: withOnlineOrdering(<OnlineOrdering />) },
			{ path: "combos", element: withOnlineOrdering(<Deals />) },
			{ path: "deals", element: withOnlineOrdering(<Combos />) },
			{
				path: "deals/:dealId/summary/:cartItemId?",
				element: withOnlineOrdering(<DealSummary />),
			},
			{
				path: "deals/:dealId/select/:slotId?",
				element: withOnlineOrdering(<DealSlotSelectorPage />),
			},
			{ path: "reservation", element: withReservation(<CreateReservation />) },
			{
				path: "reservation/complete",
				element: withReservation(<ReservationComplete />),
			},
			{ path: "checkout", element: withOnlineOrdering(<Checkout />) },
			{
				path: "checkout/complete",
				element: withOnlineOrdering(<CheckoutComplete />),
			},
			{ path: "my-orders", element: withOnlineOrdering(<MyOrders />) },
			{ path: "my-orders/:orderId", element: withOnlineOrdering(<MyOrders />) },
			{ path: "track-order", element: withOnlineOrdering(<TrackOrder />) },
			{ path: "profile", element: <Profile /> },
			{ path: "notifications", element: <Notifications /> },
			{ path: "cart", element: withOnlineOrdering(<CartPage />) },
			{ path: "order-details", element: withOnlineOrdering(<OrderDetails />) },
			{ path: "reset-password", element: <ResetPassword /> },
			{
				path: "reservation/cancel",
				element: withReservation(<CancelReservation />),
			},
			{ path: "dashboard", element: withOnlineOrdering(<OnlineOrdering />) },
			{ path: "pizza-modifier-popup", element: <PizzaModifierTestPage /> },
			{
				path: "*",
				element: <ExtraError />,
			},
		],
	},
	{
		path: "*",
		element: <ExtraError />,
	},
];
export default AppRoute;
